import React, { useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import type { LayerGeometryType, LayerRenderer, RendererCategory } from '../../../types';
import {
  FILL_STYLES,
  LINE_STYLES,
  POINT_SYMBOLS,
  SYMBOL_CATEGORIES,
  buildSymbolSvg,
  getLineDashArray,
  getPointSymbol,
  mergeCategoryStyle,
  type SymbolCategory,
} from '../../../layers/symbols';
import { ColorField, SliderField } from './SymbolProperties';

/**
 * The actual symbol a unique-value class draws with — a point icon, a line
 * swatch, or a fill+outline swatch — in place of a flat colour box. Used
 * both as this popover's trigger button and standing alone wherever a small
 * live preview of a category's current style is needed.
 */
export function CategorySymbolPreview({
  geometry,
  effective,
  className = '',
}: {
  geometry: LayerGeometryType;
  effective: LayerRenderer;
  className?: string;
}) {
  if (geometry === 'point') {
    const symbol = getPointSymbol(effective.symbolId);
    return (
      <span
        aria-hidden="true"
        className={`flex items-center justify-center ${className}`}
        dangerouslySetInnerHTML={{
          __html: buildSymbolSvg({
            path: symbol.path,
            size: 20,
            color: effective.color,
            outlineColor: effective.outlineColor,
            outlineWidth: effective.outlineWidth,
            opacity: effective.fillOpacity,
            rotation: effective.rotation ?? 0,
          }),
        }}
      />
    );
  }

  if (geometry === 'line') {
    return (
      <span aria-hidden="true" className={className}>
        <svg viewBox="0 0 36 20" className="h-full w-full" preserveAspectRatio="none">
          <line
            x1="2"
            y1="10"
            x2="34"
            y2="10"
            stroke={effective.color}
            strokeWidth={3}
            strokeOpacity={effective.fillOpacity}
            strokeDasharray={getLineDashArray(effective.lineStyle)}
            strokeLinecap="round"
          />
        </svg>
      </span>
    );
  }

  // Polygon (and unresolved geometry, which falls back to fill+outline).
  return (
    <span aria-hidden="true" className={className}>
      <svg viewBox="0 0 36 20" className="h-full w-full" preserveAspectRatio="none">
        <rect
          x="2"
          y="2"
          width="32"
          height="16"
          rx="2"
          fill={effective.color}
          fillOpacity={effective.fillStyle === 'transparent' ? 0 : effective.fillOpacity}
          stroke={effective.outlineColor}
          strokeWidth={effective.outlineWidth}
          strokeDasharray={getLineDashArray(effective.outlineStyle)}
        />
      </svg>
    </span>
  );
}

/**
 * Per-category symbol editor — replaces the old flat colour box.
 *
 * Opens an anchored popover (same positioning approach as
 * ColorSwatchPicker) scoped to ONE unique-value class, letting an admin give
 * that class its own colour AND its own symbol/line style/fill pattern —
 * different feature types in the same layer can look genuinely different,
 * not just differently coloured. Every field left untouched keeps inheriting
 * the layer's own global symbol (mergeCategoryStyle), so a class nobody has
 * customised yet renders exactly as it always has.
 */
export default function CategorySymbolPopover({
  geometry,
  renderer,
  category,
  sldOnly = false,
  label,
  onChange,
}: {
  geometry: LayerGeometryType;
  renderer: LayerRenderer;
  category: RendererCategory;
  /** GeoServer renders WMS itself and only understands SLD marks. */
  sldOnly?: boolean;
  label: string;
  onChange: (changes: Partial<RendererCategory>) => void;
}) {
  const [open, setOpen] = useState(false);
  const [symbolTab, setSymbolTab] = useState<SymbolCategory | 'All'>('All');
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const popoverRef = useRef<HTMLDivElement | null>(null);
  const [position, setPosition] = useState<{ top: number; left: number } | null>(null);

  const effective = mergeCategoryStyle(renderer, category);

  useLayoutEffect(() => {
    if (!open) {
      setPosition(null);
      return;
    }
    const anchor = buttonRef.current;
    const popover = popoverRef.current;
    if (!anchor || !popover) return;

    const anchorRect = anchor.getBoundingClientRect();
    const popRect = popover.getBoundingClientRect();
    const margin = 8;

    let top = anchorRect.bottom + 6;
    if (top + popRect.height > window.innerHeight - margin) {
      top = Math.max(margin, anchorRect.top - popRect.height - 6);
    }
    let left = anchorRect.left;
    left = Math.min(Math.max(margin, left), window.innerWidth - popRect.width - margin);

    setPosition({ top, left });
  }, [open]);

  useOutsideClose(open, buttonRef, popoverRef, () => setOpen(false));

  const availableSymbols = sldOnly ? POINT_SYMBOLS.filter((symbol) => symbol.sldMark) : POINT_SYMBOLS;
  const visibleSymbols =
    symbolTab === 'All' ? availableSymbols : availableSymbols.filter((symbol) => symbol.category === symbolTab);

  const resetOverrides = () =>
    onChange({
      symbolId: undefined,
      sldMark: undefined,
      pointSize: undefined,
      rotation: undefined,
      outlineColor: undefined,
      outlineWidth: undefined,
      fillOpacity: undefined,
      lineStyle: undefined,
      fillStyle: undefined,
      outlineStyle: undefined,
    });

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        title={`Edit symbol — ${label}`}
        aria-label={`Edit symbol for ${label}`}
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={() => setOpen((current) => !current)}
        className={`flex h-7 w-9 flex-shrink-0 cursor-pointer items-center justify-center rounded border border-slate-300 bg-white transition hover:scale-105 hover:border-slate-500 hover:shadow focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400 ${
          open ? 'ring-2 ring-blue-500' : ''
        }`}
      >
        <CategorySymbolPreview
          geometry={geometry}
          effective={effective}
          className={geometry === 'point' ? 'h-5 w-5' : 'h-4 w-6'}
        />
      </button>

      {/*
        Portalled to <body>, not rendered inline. The Symbology panel (this
        popover's ancestor) is translated (Tailwind's translate-x-* classes)
        for its slide-in/out animation, and a `transform` on an ancestor
        creates a NEW containing block for `position: fixed` descendants —
        so without the portal, "fixed" would mean "fixed to the panel", not
        the viewport, and the panel's own `overflow-hidden` would clip this
        right off. Same reasoning as LayerOptionsMenu's own portal.
      */}
      {open && createPortal(
        <div
          ref={popoverRef}
          role="dialog"
          aria-label={`Symbol editor for ${label}`}
          className="fixed z-[7000] w-64 rounded-xl border border-slate-200 bg-white p-3 shadow-[0_12px_32px_-8px_rgba(15,41,66,0.35)]"
          style={{
            top: position?.top ?? -9999,
            left: position?.left ?? -9999,
            visibility: position ? 'visible' : 'hidden',
          }}
        >
          <p className="mb-2 truncate text-[11px] font-semibold text-slate-700" title={label}>
            {label}
          </p>

          {geometry === 'point' && (
            <>
              {sldOnly && (
                <p className="mb-2 rounded bg-amber-50 px-2 py-1 text-[10px] leading-relaxed text-amber-800">
                  Only SLD-compatible symbols are shown for this service.
                </p>
              )}
              <div className="mb-1.5 flex flex-wrap gap-1">
                {(['All', ...SYMBOL_CATEGORIES] as const).map((entry) => (
                  <button
                    key={entry}
                    type="button"
                    onClick={() => setSymbolTab(entry as SymbolCategory | 'All')}
                    className={`rounded-full px-2 py-0.5 text-[9px] font-medium transition ${
                      symbolTab === entry ? 'bg-[#0F2942] text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                    }`}
                  >
                    {entry}
                  </button>
                ))}
              </div>
              <div className="mb-2 grid max-h-32 grid-cols-6 gap-1 overflow-y-auto rounded-lg border border-slate-200 p-1.5">
                {visibleSymbols.map((symbol) => {
                  const isSelected = symbol.id === (effective.symbolId ?? 'circle');
                  return (
                    <button
                      key={symbol.id}
                      type="button"
                      title={symbol.name}
                      aria-pressed={isSelected}
                      // Picking a symbol resets THIS class's outline off —
                      // see the Outline checkbox below. A fresh icon should
                      // read clean by default; a thick outline around an
                      // arbitrary gallery shape frequently looks wrong, so
                      // it is opt-in rather than inherited silently.
                      onClick={() => onChange({ symbolId: symbol.id, sldMark: symbol.sldMark, outlineWidth: 0 })}
                      className={`flex items-center justify-center rounded border p-1 transition ${
                        isSelected ? 'border-blue-500 bg-blue-50' : 'border-transparent hover:border-slate-200 hover:bg-slate-50'
                      }`}
                    >
                      <svg viewBox="0 0 24 24" className="h-4 w-4" aria-hidden="true">
                        <path d={symbol.path} fill={effective.color} stroke={effective.outlineColor} strokeWidth={0.75} strokeLinejoin="round" />
                      </svg>
                    </button>
                  );
                })}
              </div>
              <ColorField label="Colour" value={effective.color} onChange={(color) => onChange({ color })} />
              <SliderField label="Size" value={effective.pointSize} min={6} max={48} suffix="px" onChange={(pointSize) => onChange({ pointSize })} />
              <SliderField label="Rotation" value={effective.rotation ?? 0} min={0} max={360} suffix="°" onChange={(rotation) => onChange({ rotation })} />
              {/* Off by default (see the symbol gallery's own onClick above,
                  which zeroes outlineWidth the moment a symbol is chosen) —
                  an opt-in checkbox rather than an always-on outline. */}
              <label className="flex items-center justify-between py-1.5">
                <span className="text-xs text-slate-600">Outline</span>
                <input
                  type="checkbox"
                  checked={(effective.outlineWidth ?? 0) > 0}
                  onChange={(event) =>
                    onChange({ outlineWidth: event.target.checked ? renderer.outlineWidth || 2 : 0 })
                  }
                  aria-label="Show outline"
                  className="h-4 w-4 accent-blue-600"
                />
              </label>
              {(effective.outlineWidth ?? 0) > 0 && (
                <>
                  <ColorField label="Outline colour" value={effective.outlineColor} onChange={(outlineColor) => onChange({ outlineColor })} />
                  <SliderField label="Outline width" value={effective.outlineWidth} min={0.5} max={6} step={0.5} suffix="px" onChange={(outlineWidth) => onChange({ outlineWidth })} />
                </>
              )}
              <SliderField
                label="Opacity"
                value={Math.round(effective.fillOpacity * 100)}
                min={0}
                max={100}
                suffix="%"
                onChange={(value) => onChange({ fillOpacity: value / 100 })}
              />
            </>
          )}

          {geometry === 'line' && (
            <>
              <div className="mb-2 space-y-1">
                {LINE_STYLES.map((style) => {
                  const isSelected = style.id === (effective.lineStyle ?? 'solid');
                  return (
                    <button
                      key={style.id}
                      type="button"
                      aria-pressed={isSelected}
                      onClick={() => onChange({ lineStyle: style.id })}
                      className={`flex w-full items-center gap-2 rounded-lg border px-2 py-1.5 transition ${
                        isSelected ? 'border-blue-500 bg-blue-50' : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50'
                      }`}
                    >
                      <span className="w-16 flex-shrink-0 text-left text-[10px] font-medium text-slate-700">{style.name}</span>
                      <svg viewBox="0 0 100 8" className="h-2 flex-1" preserveAspectRatio="none">
                        <line x1="0" y1="4" x2="100" y2="4" stroke={effective.color} strokeWidth={3} strokeDasharray={style.dashArray} />
                      </svg>
                    </button>
                  );
                })}
              </div>
              <ColorField label="Colour" value={effective.color} onChange={(color) => onChange({ color })} />
              <SliderField label="Width" value={effective.outlineWidth} min={0.5} max={12} step={0.5} suffix="px" onChange={(outlineWidth) => onChange({ outlineWidth })} />
              <SliderField
                label="Opacity"
                value={Math.round(effective.fillOpacity * 100)}
                min={0}
                max={100}
                suffix="%"
                onChange={(value) => onChange({ fillOpacity: value / 100 })}
              />
            </>
          )}

          {geometry !== 'point' && geometry !== 'line' && (
            <>
              <div className="mb-2 grid grid-cols-2 gap-1 rounded-lg bg-slate-100 p-1">
                {FILL_STYLES.map((style) => (
                  <button
                    key={style.id}
                    type="button"
                    aria-pressed={(effective.fillStyle ?? 'solid') === style.id}
                    onClick={() => onChange({ fillStyle: style.id })}
                    className={`rounded-md px-2 py-1 text-[11px] font-medium transition ${
                      (effective.fillStyle ?? 'solid') === style.id ? 'bg-white text-blue-700 shadow-sm' : 'text-slate-500'
                    }`}
                  >
                    {style.name}
                  </button>
                ))}
              </div>
              <ColorField label="Fill" value={effective.color} onChange={(color) => onChange({ color })} />
              <SliderField
                label="Opacity"
                value={Math.round(effective.fillOpacity * 100)}
                min={0}
                max={100}
                suffix="%"
                onChange={(value) => onChange({ fillOpacity: value / 100, fillStyle: 'solid' })}
              />
              <ColorField label="Outline" value={effective.outlineColor} onChange={(outlineColor) => onChange({ outlineColor })} />
              <SliderField label="Outline width" value={effective.outlineWidth} min={0} max={10} step={0.5} suffix="px" onChange={(outlineWidth) => onChange({ outlineWidth })} />
              <label className="block py-1.5">
                <span className="text-xs text-slate-600">Outline style</span>
                <select
                  value={effective.outlineStyle ?? 'solid'}
                  onChange={(event) => onChange({ outlineStyle: event.target.value as RendererCategory['outlineStyle'] })}
                  className="mt-1 w-full rounded-lg border border-slate-200 bg-white px-2 py-1.5 text-xs outline-none focus:border-blue-400"
                >
                  {LINE_STYLES.map((style) => (
                    <option key={style.id} value={style.id}>
                      {style.name}
                    </option>
                  ))}
                </select>
              </label>
            </>
          )}

          <div className="mt-2 flex justify-between gap-1.5 border-t border-slate-100 pt-2">
            <button
              type="button"
              onClick={resetOverrides}
              className="rounded-lg border border-slate-200 px-2.5 py-1 text-[11px] font-medium text-slate-600 transition hover:bg-slate-50"
            >
              Use layer default
            </button>
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="rounded-lg bg-[#0F2942] px-3 py-1 text-[11px] font-semibold text-white transition hover:bg-[#16395C]"
            >
              Done
            </button>
          </div>
        </div>,
        document.body,
      )}
    </>
  );
}

/** Closes on outside click or Escape — shared by every anchored popover in
 *  this panel (mirrors ColorSwatchPicker's own listener setup). */
function useOutsideClose(
  open: boolean,
  buttonRef: React.RefObject<HTMLButtonElement | null>,
  popoverRef: React.RefObject<HTMLDivElement | null>,
  onClose: () => void,
) {
  React.useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (popoverRef.current?.contains(target) || buttonRef.current?.contains(target)) return;
      onClose();
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        onClose();
      }
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown, true);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown, true);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);
}
